import { FC, useState, ReactElement, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { TFunction, useTranslation } from 'next-i18next/pages'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { useUser } from '@corratech/pylot-auth-manager'
import { useWishlistActions } from '../Hooks/useWishlistActions'
import { AnimatePresence } from 'framer-motion'
import { CreateWishlist } from '../CreateWishlist'
import { WishlistCollection } from '../WishlistItems/WishlistCollection'
import BottomDrawer from '@components/common/BottomDrawer'
import CartToast from '@corratech/pylot-cart/src/CartSidebarView/components/CartToast'
import useIsMobile from '@lib/hooks/useIsMobile'
import {
    Maybe,
    WishlistItemInput,
    WishlistItemInterface,
    Wishlist
} from '@pylot-data/pylotschema'
import { Button } from '@corratech/pylot-ui'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import cn from 'classnames'
import formStyles from '@commonStyles/Forms.module.scss'
import s from '@pagestyles/Wishlist.module.scss'
import { useWishlist } from '../Hooks/use-wishlist'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { HeartCasaideas } from '@components/icons'

const WishlistSidebarView = dynamic(() => import('../WishlistSidebarView'))

export interface WishlistButtonContentProps {
    isProductInWishlist: boolean
}

export type Props = {
    isWishlistDisabled: boolean
    isConfig: boolean
    enableRemoveProductFromWishlist?: boolean
    buttonContent?: ({
        isProductInWishlist
    }: WishlistButtonContentProps) => ReactElement
    stayOnPage?: boolean
    errorWithToast?: boolean
}

const renderButtonContent = (
    t: TFunction,
    isProductInWishlist: boolean,
    enableRemoveProductFromWishlist: boolean,
    buttonContent?: ({
        isProductInWishlist
    }: WishlistButtonContentProps) => ReactElement
) => {
    if (!buttonContent) {
        return enableRemoveProductFromWishlist && isProductInWishlist
            ? t('wishlist|Remove from wishlist')
            : t('wishlist|Add to wishlist')
    }
    return buttonContent({ isProductInWishlist })
}

export const AddToWishlist: FC<WishlistItemInput & Props> = ({
    sku,
    selected_options,
    quantity,
    isWishlistDisabled,
    isConfig,
    enableRemoveProductFromWishlist = false,
    buttonContent,
    stayOnPage = false,
    errorWithToast = false
}) => {
    const { t } = useTranslation(['wishlist'])
    const { isSignedIn, loading } = useUser()
    const { handleAddWishlistAction, isLoading } = useWishlistActions()
    const [showCreateForm, setShowCreateForm] = useState(false)
    const { openAuthModal, onLoginCallback, onSignUpCallback } = useAuthUI()
    const [error, setError] = useState<boolean>(false)
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)
    const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState<boolean>(false)
    const [selectedWishlist, setSelectedWishlist] = useState<string>('')
    const [hasMounted, setHasMounted] = useState(false)
    const config = useStoreConfig()
    const { openToast, openCartToast } = useUI()
    const isMobile = useIsMobile()
    const { loading: isWishlistLoading, wishlists } = useWishlist()

    let productInWishlist: Maybe<WishlistItemInterface> | undefined
    let wishlistId: Maybe<string> | undefined
    const isButtonLoading = hasMounted && (isLoading || isWishlistLoading)

    if (enableRemoveProductFromWishlist) {
        for (const wishlist of wishlists || []) {
            productInWishlist = wishlist?.items_v2?.items.find(
                (item) => item?.product?.sku === sku
            )
            if (productInWishlist) {
                wishlistId = wishlist?.id
                break
            }
        }
    }

    const handleOpenWishlistSidebar = () => {
        setIsSidebarOpen(true)
        if (showCreateForm) setShowCreateForm(false)
    }

    const handleOpenCreateForm = () => {
        if (isBottomDrawerOpen && !isSidebarOpen) {
            setIsBottomDrawerOpen(false)
            handleOpenWishlistSidebar()
        }
        setShowCreateForm(true)
    }

    const handleCloseWishlistSidebar = () => {
        setIsSidebarOpen(false)
    }

    const handleSelectWishlist = (id: string) => {
        setSelectedWishlist(id)
        if (isMobile) {
            handleClick(Number(id))
        }
    }

    const handleDrawerOpen = () => {
        setIsBottomDrawerOpen(true)
    }
    const handleDrawerClose = () => {
        setIsBottomDrawerOpen(false)
    }

    const isProductInWishlist = !!productInWishlist

    const addProductToWishlistHandler = (id?: number) => {
        if (isMobile) {
            handleCloseWishlistSidebar()
            handleDrawerOpen()
        }
        handleAddWishlistAction(
            {
                wishlistId: id ? id : 0,
                itemOptions: [
                    {
                        sku,
                        quantity,
                        selected_options
                    }
                ]
            },
            isConfig,
            stayOnPage,
            isMobile ? handleDrawerClose : handleCloseWishlistSidebar
        )
    }

    useEffect(() => {
        setHasMounted(true)
    }, [])

    useEffect(() => {
        if (!isMobile) {
            setShowCreateForm(false)
            setSelectedWishlist('')
        }
    }, [])

    useEffect(() => {
        if (isSidebarOpen && !showCreateForm) {
            setSelectedWishlist('')
        }
    }, [wishlists])

    useEffect(() => {
        if (error && errorWithToast) {
            openToast(
                t('wishlist|Please select a variant with stock to proceed.'),
                ToastType.Danger
            )
        }
    }, [error])

    const handleClick = async (wishlistId: number) => {
        const chosenWishlist =
            wishlists &&
            wishlists?.find(
                (wishlist) => wishlist?.id === wishlistId.toString()
            )

        const productInWishlist = chosenWishlist?.items_v2?.items.find(
            (item) => item?.product?.sku === sku
        )

        if (isWishlistDisabled && !isProductInWishlist) {
            setError(true)
            return
        }
        if (productInWishlist) {
            if (isMobile) {
                openToast(
                    t('wishlist|This product is already in the wish list'),
                    ToastType.Warning,
                    true
                )
            } else {
                openCartToast(
                    t('wishlist|This product is already in the wish list'),
                    ToastType.Warning
                )
            }
        } else {
            addProductToWishlistHandler(wishlistId)
        }
    }

    const handleSignInAction = () => {
        openAuthModal()
        onLoginCallback([() => addProductToWishlistHandler()])
        onSignUpCallback([() => addProductToWishlistHandler()])
    }

    if (!config.base.wishlist.enabled) return null

    //show login modal for non logged-in users
    if (!isSignedIn) {
        return (
            <Button
                className={s['add-to-wishlist']}
                onClick={handleSignInAction}
                onKeyUp={handleSignInAction}
                /* wishlists stays undefined when guest — do not gate on it or the CTA never enables */
                disabled={hasMounted && loading}
                variant="link"
            >
                {renderButtonContent(
                    t,
                    isProductInWishlist,
                    enableRemoveProductFromWishlist,
                    buttonContent
                )}
            </Button>
        )
    }

    const createWishlistForm = !showCreateForm ? (
        <div className={s['wishlist-sidebar-create-container']}>
            <div className={s['wishlist-sidebar-empty']}>
                <HeartCasaideas />
                <div className={s['wishlist-empty-title']}>
                    {t('wishlist|You dont have wishlists yet.')}
                </div>
            </div>
            <div className={s['wishlist-sidebar-action-container']}>
                <Button
                    className={s['wishlist-sidebar-btn']}
                    onClick={() => setShowCreateForm(true)}
                >
                    {t('wishlist|Create')}
                </Button>
            </div>
        </div>
    ) : (
        <CreateWishlist
            show={showCreateForm}
            setShow={setShowCreateForm}
            onCreate={addProductToWishlistHandler}
            fromWishlistSidebar
            handleCloseWishlistSidebar={handleCloseWishlistSidebar}
        />
    )

    const wishlistCollection =
        wishlists && wishlists.length ? (
            <>
                <div className={s['wishlist-sidebar-collection']}>
                    <WishlistCollection
                        wishlists={
                            wishlists
                                ? wishlists.filter(
                                      (wishlist): wishlist is Wishlist =>
                                          wishlist !== null
                                  )
                                : []
                        }
                        handleSelectWishlist={handleSelectWishlist}
                        selectedWishlist={selectedWishlist}
                        setShowCreateForm={handleOpenCreateForm}
                        fromWishlistSidebar
                    />
                </div>
                <div className={s['wishlist-sidebar-action-container']}>
                    <Button
                        className={s['wishlist-sidebar-btn']}
                        onClick={() => handleClick(Number(selectedWishlist))}
                        disabled={selectedWishlist == ''}
                        loading={isLoading}
                    >
                        {isLoading ? t('wishlist|Adding') : t('wishlist|Add')}
                    </Button>
                </div>
            </>
        ) : (
            createWishlistForm
        )

    return (
        <>
            <div className={cn(s['add-to-wishlist'], formStyles.root)}>
                <Button
                    loading={isButtonLoading}
                    disabled={isButtonLoading}
                    variant="link"
                    onKeyUp={() =>
                        isMobile
                            ? handleDrawerOpen()
                            : handleOpenWishlistSidebar()
                    }
                    onClick={() =>
                        isMobile
                            ? handleDrawerOpen()
                            : handleOpenWishlistSidebar()
                    }
                >
                    {renderButtonContent(
                        t,
                        isProductInWishlist,
                        enableRemoveProductFromWishlist,
                        buttonContent
                    )}
                </Button>
            </div>
            {isSidebarOpen && (
                <WishlistSidebarView
                    open={isSidebarOpen}
                    onClose={handleCloseWishlistSidebar}
                    showCreateForm={showCreateForm}
                >
                    <div className={s['wishlist-sidebar-container']}>
                        {!showCreateForm
                            ? wishlistCollection
                            : createWishlistForm}
                    </div>
                </WishlistSidebarView>
            )}
            <AnimatePresence
                initial={false}
                mode="wait"
                onExitComplete={() => null}
            >
                {isBottomDrawerOpen && (
                    <BottomDrawer onClose={handleDrawerClose}>
                        <div className={s['wishlist-drawer']}>
                            <div className={s['wishlist-drawer-toast-overlay']}>
                                <CartToast />
                            </div>
                            <div className={s['wishlist-drawer-title']}>
                                {t('wishlist|My lists')}
                            </div>
                            <div className={s['wishlist-drawer-collection']}>
                                <WishlistCollection
                                    wishlists={
                                        wishlists
                                            ? wishlists.filter(
                                                  (
                                                      wishlist
                                                  ): wishlist is Wishlist =>
                                                      wishlist !== null
                                              )
                                            : []
                                    }
                                    handleSelectWishlist={handleSelectWishlist}
                                    selectedWishlist={selectedWishlist}
                                    setShowCreateForm={handleOpenCreateForm}
                                    fromWishlistSidebar
                                    isAddtoWishlistLoading={isLoading}
                                />
                            </div>
                        </div>
                    </BottomDrawer>
                )}
            </AnimatePresence>
            {isWishlistDisabled && error && !errorWithToast && (
                <div className={s['wishlist-error']}>
                    {t(
                        'wishlist|Please select a variant with stock to proceed.'
                    )}
                </div>
            )}
        </>
    )
}
